import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { UsersService } from '../../users/data-access/users.service';
import { UserDetailItem } from '../../users/data-access/users.models';

@Component({
  selector: 'app-specialist-quick-view-modal',
  standalone: true,
  imports: [QuickViewModalShellComponent, TranslatePipe],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!specialistUserId"
      [title]="title"
      [subtitle]="subtitle | t"
      [isLoading]="isLoading"
      [loadingText]="'Loading user...' | t"
      [errorText]="errorText ? (errorText | t) : null"
      (close)="close.emit()"
    >
      @if (specialist) {
        <div class="quick-view-profile">
          @if (specialist.photoUrl) {
            <img
              class="quick-view-photo"
              [src]="specialist.photoUrl"
              alt="Specialist profile photo"
            />
          }

          <div class="quick-view-name">{{ specialist.fullName }}</div>
        </div>

        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>{{ 'Clinic' | t }}</span>
            <strong>{{ specialist.clinic?.name || (specialist.clinicId ? specialist.clinicId : '—') }}</strong>
          </div>

          <div class="quick-view-field quick-view-field--wide">
            <span>{{ 'Procedure' | t }}</span>
            @if (specialist.specialistProcedureTypes.length > 0) {
              <div class="quick-view-chips">
                @for (procedure of specialist.specialistProcedureTypes; track procedure.id) {
                  <strong class="quick-view-chip">
                    {{ procedure.name }}
                  </strong>
                }
              </div>
            } @else {
              <strong>—</strong>
            }
          </div>
        </div>
      }
    </lib-quick-view-modal-shell>
  `,
})
export class SpecialistQuickViewModalComponent implements OnChanges {
  private readonly usersService = inject(UsersService);

  @Input() public isOpen = false;
  @Input() public specialistUserId: string | null = null;
  @Input() public companyIdOverride: string | null = null;
  @Input() public title = 'Specialist Quick View';
  @Input() public subtitle = 'Operational specialist context without leaving the current workflow.';

  @Output() public readonly close = new EventEmitter<void>();

  protected specialist: UserDetailItem | null = null;
  protected isLoading = false;
  protected errorText: string | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if ('isOpen' in changes || 'specialistUserId' in changes || 'companyIdOverride' in changes) {
      this.loadSpecialist();
    }
  }

  private loadSpecialist(): void {
    const specialistUserId = this.specialistUserId?.trim() ?? '';

    if (!this.isOpen || !specialistUserId) {
      this.specialist = null;
      this.errorText = null;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorText = null;

    this.usersService.getDetailById(
      specialistUserId,
      this.companyIdOverride?.trim() || undefined,
    ).subscribe({
      next: (item) => {
        this.specialist = item;
        this.isLoading = false;
      },
      error: () => {
        this.specialist = null;
        this.errorText = 'Failed to load user';
        this.isLoading = false;
      },
    });
  }
}
